import cx from 'classnames';
import {map} from 'lodash';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, InformationModal, IconName, Tooltip, ParticipantName} from 'components';
import {useUserSession} from 'hooks';
import {AgentSession, AnyParticipant, ClientParticipantData} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {platformUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.SCREEN_SHARING_DIALOG;

type Props = {
    readonly open: boolean;
    readonly session: AgentSession;
    readonly onClose: (participantId?: string) => void;
};

export const ScreenSharingDialog: React.FC<Props> = ({open, onClose}: Props) => {
    const {t} = useTranslation();
    const {participants, localParticipant} = useUserSession();

    const isDisabledForParticipant = useCallback((participant: AnyParticipant) => {
        const isLocal = participant.id === localParticipant?.id;
        if (isLocal) {
            const mediaDevices = navigator.mediaDevices as any;
            const noAPI = !mediaDevices || !mediaDevices.getDisplayMedia;
            const isRestricted = platformUtils.isOsNoScreenSharing() || platformUtils.isSafari();
            return noAPI || isRestricted;
        }
        if (participant.role === 'client') {
            const data = participant.data as ClientParticipantData;
            const isSafari = data.clientInfo?.browser && platformUtils.isSafari(data.clientInfo?.browser);
            const noOsScreenSharing = data.clientInfo?.os && platformUtils.isOsNoScreenSharing(data.clientInfo?.os);
            return isSafari || noOsScreenSharing;
        }
    }, [localParticipant]);

    const renderParticipant = useCallback((
        participant: AnyParticipant, label: string | React.ReactElement, icon: IconName, disabledTooltip: string
    ) => {
        if (!participant) {
            return null;
        }
        const disabled = isDisabledForParticipant(participant);
        return (
            <div
                key={participant.id}
                className={cx(
                    'tw-flex tw-items-center tw-justify-between tw-p-1',
                    {
                        'tw-text-dark tw-cursor-pointer hover:tw-bg-primary-light tw-bg-opacity-25': !disabled,
                        'tw-text-default-dimmed': disabled
                    }
                )}
                onClick={disabled ? null : () => onClose(participant.id)}
            >
                <div>{label}</div>
                {disabled && <Tooltip header={disabledTooltip}><Icon name='share-screen-off' color='default-dimmed' /></Tooltip>}
                {!disabled && <Icon name={icon} color='dark' />}
            </div>
        );
    }, [isDisabledForParticipant, onClose]);

    return (
        <InformationModal
            open={open}
            maxWidth='sm'
            title={t(T_KEYS.TITLE)}
            fullWidth
            onClose={() => onClose()}
        >
            <div>
                <div className='tw-ts-h5-bold'>{t(T_KEYS.LOCAL_SCREEN.HEADER)}</div>
                {renderParticipant(
                    localParticipant,
                    t(T_KEYS.LOCAL_SCREEN.DESCRIPTION),
                    'share-screen-out',
                    t(T_KEYS.LOCAL_SCREEN.TOOLTIP_DISABLED),
                )}
            </div>
            <div>
                <div className='tw-ts-h5-bold'>{t(T_KEYS.REMOTE_SCREEN.HEADER)}</div>
                <div className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.REMOTE_SCREEN.DESCRIPTION)}</div>
                {map(participants, (participant) => (
                    renderParticipant(
                        participant,
                        <ParticipantName participant={participant} nameOnly />,
                        'share-screen-in',
                        t(T_KEYS.REMOTE_SCREEN.TOOLTIP_DISABLED),
                    )
                ))}
            </div>
        </InformationModal>
    );
};
